import { useCallback, useEffect, useRef, useState } from "react";
import type { SyncDoc, PublicUser } from "../sync/types.ts";
import { mergeDocs } from "../sync/merge.ts";

const KEY_TOKEN = "t24.token";
/** Quiet period after a change before the document is pushed. */
const PUSH_DEBOUNCE_MS = 1500;
/** Background re-sync while the tab stays open. */
const POLL_MS = 60_000;

export type SyncStatus = "signedOut" | "idle" | "syncing" | "error";

export interface SyncApi {
  user: PublicUser | null;
  status: SyncStatus;
  /** Error code from the last failed call, e.g. "invalid_credentials". */
  error: string | null;
  lastSyncedAt: number | null;
  signIn: (username: string, password: string) => Promise<boolean>;
  signUp: (username: string, password: string) => Promise<boolean>;
  signOut: () => Promise<void>;
  syncNow: () => Promise<void>;
}

async function call<T>(
  path: string,
  init: RequestInit & { token?: string | null } = {}
): Promise<{ ok: true; data: T } | { ok: false; error: string }> {
  const { token, headers, ...rest } = init;
  try {
    const res = await fetch(path, {
      ...rest,
      headers: {
        "content-type": "application/json",
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: (body as any)?.error ?? `http_${res.status}` };
    return { ok: true, data: body as T };
  } catch {
    return { ok: false, error: "network_error" };
  }
}

/**
 * Keeps the local study document in step with the signed-in account.
 *
 * One round trip does both directions: the server merges what it is sent with
 * what it holds and returns the result, which becomes the new local document.
 * Merging is per question and timestamp-based, so two devices used offline
 * both keep their work.
 */
export function useSync(doc: SyncDoc, setDoc: (doc: SyncDoc) => void): SyncApi {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(KEY_TOKEN));
  const [user, setUser] = useState<PublicUser | null>(null);
  const [status, setStatus] = useState<SyncStatus>("signedOut");
  const [error, setError] = useState<string | null>(null);
  const [lastSyncedAt, setLastSyncedAt] = useState<number | null>(null);

  // Read through refs so the debounce effect does not restart on every change.
  const docRef = useRef(doc);
  docRef.current = doc;
  const setDocRef = useRef(setDoc);
  setDocRef.current = setDoc;
  /** Serialized document as the server last confirmed it. */
  const lastPushed = useRef<string>("");
  const inFlight = useRef(false);

  const applyToken = useCallback((next: string | null) => {
    setToken(next);
    if (next) localStorage.setItem(KEY_TOKEN, next);
    else localStorage.removeItem(KEY_TOKEN);
  }, []);

  const syncNow = useCallback(async () => {
    if (!token || inFlight.current) return;
    inFlight.current = true;
    setStatus("syncing");

    const payload = JSON.stringify({ doc: docRef.current });
    const res = await call<{ doc: SyncDoc }>("/api/sync", {
      method: "POST",
      token,
      body: payload,
    });
    inFlight.current = false;

    if (!res.ok) {
      if (res.error === "unauthorized") {
        applyToken(null);
        setUser(null);
        setStatus("signedOut");
      } else {
        setStatus("error");
      }
      setError(res.error);
      return;
    }

    // Fold the server's answer in rather than overwriting: the user may have
    // answered another question while the request was in flight.
    const merged = mergeDocs(docRef.current, res.data.doc);
    lastPushed.current = JSON.stringify(merged);
    setDocRef.current(merged);
    setLastSyncedAt(Date.now());
    setStatus("idle");
    setError(null);
  }, [token, applyToken]);

  const authenticate = useCallback(
    async (path: string, username: string, password: string) => {
      setStatus("syncing");
      setError(null);
      const res = await call<{ token: string; user: PublicUser }>(path, {
        method: "POST",
        body: JSON.stringify({ username, password }),
      });
      if (!res.ok) {
        setError(res.error);
        setStatus("signedOut");
        return false;
      }
      applyToken(res.data.token);
      setUser(res.data.user);
      setStatus("idle");
      return true;
    },
    [applyToken]
  );

  const signIn = useCallback(
    (u: string, p: string) => authenticate("/api/login", u, p),
    [authenticate]
  );
  const signUp = useCallback(
    (u: string, p: string) => authenticate("/api/register", u, p),
    [authenticate]
  );

  const signOut = useCallback(async () => {
    if (token) await call("/api/logout", { method: "POST", token });
    applyToken(null);
    setUser(null);
    setStatus("signedOut");
    setLastSyncedAt(null);
    lastPushed.current = "";
  }, [token, applyToken]);

  // Restore the session on load, then pull straight away.
  useEffect(() => {
    if (!token) {
      setStatus("signedOut");
      return;
    }
    let cancelled = false;
    (async () => {
      const res = await call<{ user: PublicUser }>("/api/me", { token });
      if (cancelled) return;
      if (!res.ok) {
        if (res.error === "unauthorized") applyToken(null);
        setStatus(res.error === "unauthorized" ? "signedOut" : "error");
        return;
      }
      setUser(res.data.user);
      setStatus("idle");
      void syncNow();
    })();
    return () => { cancelled = true; };
  }, [token, applyToken, syncNow]);

  // Push local edits after a quiet period.
  useEffect(() => {
    if (!token) return;
    if (JSON.stringify(doc) === lastPushed.current) return;
    const timer = setTimeout(() => void syncNow(), PUSH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [doc, token, syncNow]);

  // Catch up when the tab comes back, and poll gently while it is open.
  useEffect(() => {
    if (!token) return;
    const onFocus = () => void syncNow();
    window.addEventListener("focus", onFocus);
    const timer = setInterval(onFocus, POLL_MS);
    return () => {
      window.removeEventListener("focus", onFocus);
      clearInterval(timer);
    };
  }, [token, syncNow]);

  return { user, status, error, lastSyncedAt, signIn, signUp, signOut, syncNow };
}
